import classNames from "classnames"
import React, { useState, useRef } from "react"

import { useTranslation } from "@bounce/i18n"
import { useOnClickOutside } from "@bounce/web-components"

import { STORE_TYPES, HIGH_RATING } from "@/components/LuggageStorage/StoreListSection/constants"

import ChevronDownIcon from "@bounce/assets/icons/ChevronDown.svg"
import StarIcon from "@bounce/assets/icons/Star-filled.svg"

type FilterChipsProps = {
  className?: string
  onChipClick: (value: string) => void
  onDropdownSelect?: (value: string) => void
  selectedValues: string[]
  selectedTypes: string[]
}

export const FilterChips = ({
  className,
  onChipClick,
  onDropdownSelect,
  selectedValues,
  selectedTypes,
}: FilterChipsProps) => {
  const [dropdownOpen, setDropdownOpen] = useState<string | null>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const storeTypeChipRef = useRef<HTMLButtonElement>(null)
  const { t } = useTranslation("marketing-ls-city")

  useOnClickOutside({
    ref: dropdownRef,
    handler: (e: Event) => {
      if (storeTypeChipRef.current?.contains(e.target as Node)) return
      setDropdownOpen(null)
    },
  })

  const filterItems = [
    {
      label: t("cmp.filterChips.label.storeTypes", "Store types"),
      value: "type",
      hasDropdown: true,
      iconFn: (isSelected: boolean) => (
        <ChevronDownIcon
          className={classNames("size-4 transition-transform", {
            "text-primary-700": isSelected,
            "text-neutral-800": !isSelected,
            "rotate-180": dropdownOpen,
          })}
        />
      ),
    },
    {
      label: t("cmp.filterChips.label.highRatingV2", "{HIGH_RATING}+ and above", {
        HIGH_RATING: HIGH_RATING,
      }),
      value: "highRating",
      iconFn: (isSelected: boolean) => (
        <StarIcon
          className={classNames("size-4", {
            "text-primary-700": isSelected,
            "text-neutral-800": !isSelected,
          })}
        />
      ),
    },
    {
      label: t("cmp.filterChips.label.open247", "Open 24/7"),
      value: "open247",
    },
    {
      label: t("cmp.filterChips.label.openNow", "Open now"),
      value: "openNow",
    },
    {
      label: t("cmp.filterChips.label.availability", "Available now"),
      value: "availability",
    },
  ]

  const handleChipClick = (value: string, hasDropdown?: boolean) => {
    if (hasDropdown) {
      setDropdownOpen(prev => (prev === value ? null : value))
    } else {
      onChipClick(value)
    }
  }

  const handleDropdownSelect = (storeType: string) => {
    onDropdownSelect?.(storeType)
  }

  return (
    <div className={classNames("relative mx-auto flex w-full max-w-screen-lg flex-col", className)}>
      <div className="flex w-full flex-nowrap gap-2 overflow-x-auto [-ms-overflow-style:none] [scrollbar-width:none] md:flex-wrap lg:px-0">
        {filterItems.map(({ label, value, hasDropdown, iconFn }) => {
          const isSelected = value === "type" ? selectedTypes.length > 0 : selectedValues.includes(value)

          return (
            <button
              key={value}
              ref={value === "type" ? storeTypeChipRef : undefined}
              onClick={() => handleChipClick(value, hasDropdown)}
              className={classNames(
                "flex items-center gap-2 whitespace-nowrap rounded-full border px-4 py-2 text-sm font-medium transition-colors",
                isSelected
                  ? "border-primary-700 bg-primary-200 text-primary-700"
                  : "border-neutral-300 bg-white text-neutral-800",
              )}>
              <span>{label}</span>
              {iconFn && hasDropdown && iconFn(isSelected)}
            </button>
          )
        })}

        {!!dropdownOpen && (
          <div
            ref={dropdownRef}
            className="absolute inset-x-4 top-full z-10 mt-2 w-auto max-w-md rounded-xl border border-neutral-200 bg-white p-4 shadow-lg sm:left-0 sm:right-auto sm:w-full">
            <div className="flex flex-wrap gap-2">
              {STORE_TYPES.map(type => {
                const isSelected = selectedTypes.includes(type)
                return (
                  <button
                    key={type}
                    onClick={() => handleDropdownSelect(type)}
                    className={classNames(
                      "flex items-center gap-2 rounded-full border px-4 py-2 text-sm font-medium transition-colors",
                      isSelected
                        ? "border-primary-700 bg-primary-200 text-primary-700"
                        : "border-neutral-300 bg-white text-neutral-800 hover:bg-neutral-100",
                    )}>
                    {type}
                  </button>
                )
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
